import { useTranslation } from 'react-i18next';
import { IconPrinter } from '@tabler/icons-react';
import { useHistory, useLocation } from 'react-router-dom';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { useForm } from '@mantine/form';
import CatalogEditCreateHeader from '../../components/CatalogEditCreateHeader/CatalogEditCreateHeader';
import CatalogEditCreateFooter from '../../components/CatalogEditCreateFooter/CatalogEditCreateFooter';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { showNotification } from '../../utils/Notification/Notification';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useCreateCashupReport } from '../../hooks/cashups/mutations';
import { useGetStoreById } from '../../hooks/stores/queries';
import CreateEditForm from './CreateEditForm';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useSelector } from 'react-redux';
import { clearInitialCashStorageRecord } from '../../utils/initialCash';
import { useRef, useState } from 'react';
import useCanUseMercadoPago from '../../hooks/useCanUseMercadoPago';
import {
  closeCashupReportFallbackTab,
  reserveCashupReportFallbackTab,
  tryPrintCashupReportEscPos,
} from '../../utils/cashupReportEscPos';

const CreatePage = () => {
  const { t } = useTranslation([
    'CashupsPage',
    'CommonButtons',
    'FormValidator',
    'Notifications',
  ]);
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state: any) => state.user);
  const userId = user?.user?._id || user?._id || '';
  const showCashupTotals = user?.permission?.hideCashupTotals?.canView === true;
  const storeId = location?.state?.storeID;
  const { mutateAsync: createAsync, isPending: isCreationPending } = useCreateCashupReport();
  const { data: store, isLoading: isGetStoreLoading } = useGetStoreById(!!storeId, storeId || '');
  const { storeCanUseMP } = useCanUseMercadoPago(storeId || '');
  const processingLockRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isSubmitBusy = isCreationPending || isProcessing;
  const reconciliationMode =
    store?.cashUpReportConfig?.reconciliationMode || 'all_payment_methods';
  const showNonCashSummary = store?.cashUpReportConfig?.showNonCashPaymentSummary ?? true;
  const isCashOnly = reconciliationMode === 'cash_only';
  const form = useForm({
    initialValues: {
      storeId: storeId,
      cashierBox: '',
      startingCash: 0,
      declaredCash: 0,
      declaredCard: 0,
      declaredTransfer: 0,
      declaredMercadoPago: 0,
      cashToBank: 0,
      notes: ''
    },
    validate: {
      cashierBox: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      startingCash: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      declaredCash: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      declaredCard: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      declaredTransfer: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      cashToBank: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      }
    }
  });

  type FormValues = typeof form.values;
  const normalizeCashupValues = (values: FormValues): FormValues => {
    if (!isCashOnly) {
      return values;
    }

    return {
      ...values,
      declaredCard: 0,
      declaredTransfer: 0,
      declaredMercadoPago: 0
    };
  };

  const submit = async (values: FormValues, shouldPrint = false) => {
    if (processingLockRef.current) return;
    processingLockRef.current = true;
    const fallbackWindow = shouldPrint ? reserveCashupReportFallbackTab() : undefined;
    setIsProcessing(true);

    try {
      const normalizedValues = normalizeCashupValues(values);
      const createdCashup = await createAsync({
        ...normalizedValues,
        id: normalizedValues.cashierBox,
      });
      if (userId && normalizedValues.storeId) {
        clearInitialCashStorageRecord({
          storeId: normalizedValues.storeId,
          userId,
        });
      }
      showNotification({ type: 'Default', title: t('Notifications:success') });

      if (shouldPrint) {
        try {
          if (!createdCashup) {
            throw new Error('The created cashup report was not returned.');
          }
          await tryPrintCashupReportEscPos({
            reportData: createdCashup,
            store,
            storeCanUseMP,
            includeDetailed: true,
            t,
            failureTitle: t('CashupsPage:savedButPrintFailed'),
            fallbackWindow,
            openBrowserFallback: true,
          });
        } catch {
          closeCashupReportFallbackTab(fallbackWindow);
          showNotification({
            type: 'Error',
            title: t('CashupsPage:savedButPrintFailed'),
          });
        }
      }

      history.goBack();
    } catch {
      closeCashupReportFallbackTab(fallbackWindow);
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    } finally {
      processingLockRef.current = false;
      setIsProcessing(false);
    }
  };

  const confirmSubmit = async (values: FormValues, shouldPrint = false) =>
    ConfirmationModal({
      title: t('CashupsPage:confirmSubmit.title'),
      onConfirm: async () => await submit(values, shouldPrint),
      onCancel: () => {}
    });

  const submitAndPrint = form.onSubmit((values) => confirmSubmit(values, true));

  if (!storeId || !location.state || !location.state.storeID) {
    history.goBack();
    return <LoaderOverlay />;
  }

  if (isCreationPending || isGetStoreLoading) {
    return <LoaderOverlay />;
  }

  return (
    <CatalogContainer>
      <form onSubmit={form.onSubmit((values) => confirmSubmit(values, false))}>
        <CatalogEditCreateHeader title={t('CashupsPage:title')} />
        <CreateEditForm
          form={form}
          storeId={storeId}
          isCashOnly={isCashOnly}
          showNonCashSummary={showNonCashSummary}
        />
        <CatalogEditCreateFooter
          loading={isSubmitBusy}
          disableSubmit={isSubmitBusy}
          secondarySubmitActions={
            showCashupTotals
              ? [
                  {
                    key: 'save-and-print',
                    label: t('CommonButtons:saveAndPrint'),
                    icon: <IconPrinter size={16} />,
                    onClick: () => void submitAndPrint(),
                    disabled: isSubmitBusy,
                  },
                ]
              : undefined
          }
          submitMenuAriaLabel={t('CommonButtons:saveOptions')}
        />
      </form>
    </CatalogContainer>
  );
};

export default CreatePage;
